import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  User as UserIcon,
  Check,
  Clock,
  Mail,
  Search,
  Bell,
  CheckCircle2,
  Loader2,
  Handshake,
  Package,
  Calendar,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useRequireAuth } from "../hooks/useAuth";
import {
  useNotifications,
  useMarkNotificationRead,
} from "../hooks/useNetworking";
import { useNetworkUsers } from "../hooks/useUsers";
import { useCreatePackage, useInviteCollaborator, usePackages } from "../hooks/usePackages";
import { cn } from "../lib/utils";
import { CreatePackageWithCollaboratorModal } from "../components/packages/CreatePackageWithCollaboratorModal";
import type { User, Notification, Package as PackageType, PackageCollaborator } from "../types/database";

const roleLabels: Record<string, string> = {
  "Operator": "Operatore",
  "operator": "Operatore",
  "LR": "Rappresentante Legale",
  "admin": "Amministratore",
  "collaborator": "Collaboratore",
  "owner": "Proprietario",
  "viewer": "Visualizzatore",
};

const getRoleLabel = (role: string | null | undefined): string => {
  if (!role) return "";
  return roleLabels[role] || role;
};

export function Networking() {
  const navigate = useNavigate();
  const { isLoading: authLoading } = useRequireAuth();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"discover" | "pending" | "active">("discover");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);

  // Queries
  const { data: notifications = [] } = useNotifications();
  const { data: packages = [] } = usePackages();
  const {
    data: usersData,
    isLoading: usersLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useNetworkUsers();

  // Flatten paginated users
  const networkUsers = useMemo(() => {
    return usersData?.pages.flatMap(page => page.data) || [];
  }, [usersData]);

  const totalUsersCount = usersData?.pages[0]?.totalCount || 0;

  // Mutations
  const createPackage = useCreatePackage();
  const inviteCollaborator = useInviteCollaborator();
  const markNotificationRead = useMarkNotificationRead();

  // Get pending and active collaborations from packages
  const pendingCollaborations = useMemo(() => {
    const pending: { package: PackageType; collaborator: PackageCollaborator }[] = [];
    packages.forEach(pkg => {
      pkg.collaborators?.forEach(collab => {
        if (collab.status === "pending") {
          pending.push({ package: pkg, collaborator: collab });
        }
      });
    });
    return pending;
  }, [packages]);

  const activeCollaborations = useMemo(() => {
    const active: { package: PackageType; collaborator: PackageCollaborator }[] = [];
    packages.forEach(pkg => {
      pkg.collaborators?.forEach(collab => {
        if (collab.status === "accepted" && collab.role !== "owner") {
          active.push({ package: pkg, collaborator: collab });
        }
      });
    });
    return active;
  }, [packages]);

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.is_read),
    [notifications]
  );

  // Filter users
  const filteredUsers = useMemo(() => {
    let filtered = networkUsers;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(query) ||
          user.last_name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.role?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [networkUsers, searchQuery]);

  // Handlers
  const handleCreatePackageWithCollaborator = async (data: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    collaboratorId: string;
  }) => {
    try {
      // Create the package
      const pkg = await createPackage.mutateAsync({
        name: data.name,
        description: data.description,
        start_date: data.start_date,
        end_date: data.end_date,
      });

      // Invite the collaborator
      await inviteCollaborator.mutateAsync({
        packageId: pkg.id,
        userId: data.collaboratorId,
        role: "collaborator",
      });

      // Close modal and navigate to package detail
      setSelectedUser(null);
      navigate(`/packages?id=${pkg.id}`);
    } catch (error) {
      console.error("Error creating package with collaborator:", error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100dvh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100dvh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-[#284899]" />
              Networking
            </h1>
            <p className="text-muted-foreground mt-1">
              Connettiti con altri utenti per creare pianificazione di attività congiunte
            </p>
          </div>

          {/* Notifications Bell */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative"
            >
              <Bell className="h-4 w-4 mr-2" />
              Notifiche
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#284899] text-white text-sm rounded-full flex items-center justify-center">
                  {unreadNotifications.length}
                </span>
              )}
            </Button>

            {/* Notifications Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl shadow-2xl border border-border bg-card z-50"
                >
                  <div className="p-3 border-b border-border">
                    <h3 className="font-semibold text-sm">Notifiche</h3>
                  </div>
                  {notifications.length > 0 ? (
                    <div className="divide-y divide-border">
                      {notifications.slice(0, 10).map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onMarkRead={() => markNotificationRead.mutate(notification.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-muted-foreground text-sm">
                      Nessuna notifica
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("pending")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#284899] transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#284899]/10 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-[#284899]" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">{pendingCollaborations.length}</p>
                    <p className="text-sm text-muted-foreground">Inviti in attesa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("active")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500 transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Handshake className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">{activeCollaborations.length}</p>
                    <p className="text-sm text-muted-foreground">Collaborazioni attive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="group cursor-pointer"
            onClick={() => setActiveTab("discover")}
          >
            <Card className="relative overflow-hidden transition-all duration-200 hover:scale-[1.02]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500 transition-all duration-200 group-hover:h-1.5" />
              <CardContent className="p-5 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold tracking-tight">
                      {usersLoading ? "..." : (searchQuery ? filteredUsers.length : totalUsersCount || filteredUsers.length)}
                    </p>
                    <p className="text-sm text-muted-foreground">Utenti disponibili</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2"
        >
          {[
            { id: "discover", label: "Scopri Utenti", icon: Search },
            { id: "pending", label: "In Attesa", icon: Clock, count: pendingCollaborations.length },
            { id: "active", label: "Attive", icon: CheckCircle2, count: activeCollaborations.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                activeTab === tab.id
                  ? "bg-[#284899] text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn(
                  "px-1.5 py-0.5 text-sm rounded-full",
                  activeTab === tab.id ? "bg-white/20" : "bg-[#284899]/20 text-[#284899]"
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === "discover" && (
            <motion.div
              key="discover"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {/* Search */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <Input
                  placeholder="Cerca per nome, email o ruolo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={<Search className="h-4 w-4" />}
                />
              </motion.div>

              {/* Results count */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-sm text-muted-foreground"
              >
                {usersLoading ? (
                  "Caricamento..."
                ) : (
                  <>
                    {filteredUsers.length} utent{filteredUsers.length === 1 ? "e" : "i"}
                    {searchQuery && ` trovat${filteredUsers.length === 1 ? "o" : "i"}`}
                    {searchQuery && ` per "${searchQuery}"`}
                    {totalUsersCount > networkUsers.length && !searchQuery && (
                      <span className="text-sm ml-1">
                        (caricati {networkUsers.length} di {totalUsersCount})
                      </span>
                    )}
                  </>
                )}
              </motion.p>

              {/* Virtualized Users Grid */}
              <VirtualizedUsersGrid
                users={filteredUsers}
                isLoading={usersLoading}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                onFetchNextPage={fetchNextPage}
                onSelectUser={setSelectedUser}
              />
            </motion.div>
          )}

          {activeTab === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {pendingCollaborations.length > 0 ? (
                <div className="space-y-3">
                  {pendingCollaborations.map(({ package: pkg, collaborator }) => (
                    <PendingCollaborationCard
                      key={collaborator.id}
                      package={pkg}
                      collaborator={collaborator}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Clock className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessun invito in attesa</h3>
                    <p className="text-muted-foreground">
                      Gli inviti che invii appariranno qui fino a quando non vengono accettati
                    </p>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === "active" && (
            <motion.div
              key="active"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {activeCollaborations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeCollaborations.map(({ package: pkg, collaborator }) => (
                    <ActiveCollaborationCard
                      key={collaborator.id}
                      package={pkg}
                      collaborator={collaborator}
                      onClick={() => navigate(`/packages?id=${pkg.id}`)}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Handshake className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessuna collaborazione</h3>
                    <p className="text-muted-foreground mb-4">
                      Non hai ancora collaborazioni attive con altri utenti
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab("discover")}
                    >
                      Scopri utenti
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Package Modal */}
        <AnimatePresence>
          {selectedUser && (
            <CreatePackageWithCollaboratorModal
              collaborator={selectedUser}
              onClose={() => setSelectedUser(null)}
              onCreate={handleCreatePackageWithCollaborator}
              isCreating={createPackage.isPending || inviteCollaborator.isPending}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function UserCard({ user, onSelect }: { user: User; onSelect: () => void }) {
  const userName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "Utente";

  return (
    <Card className="hover:border-[#284899]/50 transition-all hover:shadow-lg group">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#284899]/20 flex items-center justify-center flex-shrink-0">
            <UserIcon className="h-5 w-5 text-[#284899]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate group-hover:text-[#284899] transition-colors">
              {userName}
            </h3>
            {user.role && (
              <Badge variant="secondary" className="mt-1">
                {getRoleLabel(user.role)}
              </Badge>
            )}
          </div>
        </div>

        {user.email && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1 truncate">
            <Mail className="h-3 w-3 flex-shrink-0" />
            {user.email}
          </p>
        )}

        <div className="flex items-center gap-2 mt-2">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Registrato il {format(new Date(user.created_at), "dd/MM/yyyy", { locale: it })}
          </span>
        </div>

        <Button size="sm" className="w-full mt-3" onClick={onSelect}>
          <Package className="h-4 w-4 mr-2" />
          Crea Pianificazione
        </Button>
      </CardContent>
    </Card>
  );
}

function PendingCollaborationCard({
  package: pkg,
  collaborator,
}: {
  package: PackageType;
  collaborator: PackageCollaborator;
}) {
  const userName = [collaborator.user?.first_name, collaborator.user?.last_name]
    .filter(Boolean)
    .join(" ") || "Utente";

  return (
    <Card className="border-[#284899]/30 bg-[#284899]/5">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#284899]/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-[#284899]" />
            </div>
            <div>
              <h3 className="font-semibold">{userName}</h3>
              <p className="text-sm text-muted-foreground">
                Pianificazione: {pkg.name}
              </p>
            </div>
          </div>
          <Badge variant="secondary" className="bg-[#284899]/20 text-[#1e3570]">
            In attesa
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Invitato il {format(new Date(collaborator.invited_at), "dd/MM/yyyy", { locale: it })}
        </p>
      </CardContent>
    </Card>
  );
}

function ActiveCollaborationCard({
  package: pkg,
  collaborator,
  onClick,
}: {
  package: PackageType;
  collaborator: PackageCollaborator;
  onClick: () => void;
}) {
  const userName = [collaborator.user?.first_name, collaborator.user?.last_name]
    .filter(Boolean)
    .join(" ") || "Utente";

  return (
    <Card
      className="border-emerald-500/30 bg-emerald-500/5 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Handshake className="h-6 w-6 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">{userName}</h3>
              <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Pianificazione: {pkg.name}
            </p>
            {collaborator.user?.email && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Mail className="h-3 w-3" />
                {collaborator.user.email}
              </p>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground mt-3">
          Collaborazione attiva dal{" "}
          {format(new Date(collaborator.responded_at || collaborator.invited_at), "dd/MM/yyyy", { locale: it })}
        </p>
      </CardContent>
    </Card>
  );
}

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: () => void;
}) {
  const getIcon = () => {
    switch (notification.type) {
      case "collaboration_request":
        return <UserPlus className="h-4 w-4 text-[#284899]" />;
      case "collaboration_response":
        return <Check className="h-4 w-4 text-emerald-500" />;
      case "package_invite":
        return <Mail className="h-4 w-4 text-blue-500" />;
      case "package_approved":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "seasonality_alert":
        return <Bell className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div
      className={cn(
        "p-3 hover:bg-secondary/50 transition-colors cursor-pointer",
        !notification.is_read && "bg-[#284899]/5"
      )}
      onClick={onMarkRead}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm", !notification.is_read && "font-medium")}>
            {notification.title}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {format(new Date(notification.created_at), "dd MMM, HH:mm", { locale: it })}
          </p>
        </div>
        {!notification.is_read && (
          <div className="w-2 h-2 rounded-full bg-[#284899] flex-shrink-0 mt-1.5" />
        )}
      </div>
    </div>
  );
}

// ============================================
// SKELETON COMPONENTS
// ============================================

function UserCardSkeleton() {
  return (
    <Card className="animate-pulse">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-4 bg-secondary rounded w-3/4" />
            <div className="h-3 bg-secondary rounded w-1/2" />
          </div>
        </div>
        <div className="h-3 bg-secondary rounded w-2/3 mt-3" />
        <div className="h-3 bg-secondary rounded w-1/3 mt-2" />
        <div className="h-8 bg-secondary rounded mt-3" />
      </CardContent>
    </Card>
  );
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <UserCardSkeleton key={i} />
      ))}
    </div>
  );
}

// ============================================
// VIRTUALIZED GRID (Window Scroll)
// ============================================

const ESTIMATED_ROW_HEIGHT = 200;
const COLUMNS = { sm: 1, md: 2, lg: 3 };

function VirtualizedUsersGrid({
  users,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  onSelectUser,
}: {
  users: User[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  onFetchNextPage: () => void;
  onSelectUser: (user: User) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(3);

  // Update columns based on window width
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 768) setColumns(COLUMNS.sm);
      else if (width < 1024) setColumns(COLUMNS.md);
      else setColumns(COLUMNS.lg);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Calculate rows
  const rowCount = Math.ceil(users.length / columns);

  // Get scroll margin (distance from top of page to container)
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setScrollMargin(containerRef.current.offsetTop);
    }
  }, []);

  // Use window virtualizer for page scroll with dynamic measurement
  const virtualizer = useWindowVirtualizer({
    count: rowCount + (hasNextPage ? 1 : 0),
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 5,
    scrollMargin,
  });

  const measureElement = virtualizer.measureElement;

  // Infinite scroll - load more when approaching bottom
  useEffect(() => {
    const handleScroll = () => {
      if (isFetchingNextPage || !hasNextPage) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop + windowHeight) / documentHeight;

      if (scrolledPercentage > 0.8) {
        onFetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFetchingNextPage, hasNextPage, onFetchNextPage]);

  if (isLoading) {
    return <SkeletonGrid count={9} />;
  }

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessun risultato</h3>
          <p className="text-muted-foreground">
            Nessun utente trovato con i filtri selezionati
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={containerRef}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const isLoaderRow = virtualRow.index === rowCount;
          const startIndex = virtualRow.index * columns;
          const rowUsers = users.slice(startIndex, startIndex + columns);

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                  {isFetchingNextPage && (
                    <>
                      <UserCardSkeleton />
                      <UserCardSkeleton />
                      <UserCardSkeleton />
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                  {rowUsers.map((user) => (
                    <UserCard
                      key={user.id}
                      user={user}
                      onSelect={() => onSelectUser(user)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-[#284899]" />
          <span className="ml-2 text-sm text-muted-foreground">Caricamento...</span>
        </div>
      )}
    </div>
  );
}
